import { useCallback, useEffect, useState } from "react";
import { Building2, CheckCircle2, Download, LifeBuoy, MapPin, ScrollText, Unplug, Webhook, XCircle } from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, formatDateTime as stamp } from "../../utils";
import AppIcon from "../../components/ui/AppIcon";
import Card from "../../components/ui/Card";
import EmptyState from "../../components/ui/EmptyState";
import JsonBlock from "../../components/ui/JsonBlock";
import ListFilters from "../../components/ui/ListFilters";
import Pagination from "../../components/ui/Pagination";
import Skeleton from "../../components/ui/Skeleton";
import Stat from "../../components/ui/Stat";
import { useToast } from "../../components/ui/Toast";
import useListQuery, { useSearchInput } from "../../hook/useListQuery";
import { ago, CopyId, Drawer, DrawerLoading, InfoGrid, Pill, Section, useOpenParam } from "../../components/directory/shared";

/**
 * Every webhook the marketplace sent us — installs, uninstalls and support
 * ticket events — exactly as it arrived. Read-only; kept 90 days.
 *
 * The detail panel also says whether the event's install row exists, which
 * answers "the marketplace says it's installed — did it reach us?".
 */

const DEFAULTS = { page: 1, limit: 50, appId: "", type: "", installType: "", since: "", q: "" };

const TypePill = ({ type }) =>
  type === "INSTALL" ? (
    <Pill tone="emerald" icon={Download}>Install</Pill>
  ) : type === "UNINSTALL" ? (
    <Pill tone="rose" icon={Unplug}>Uninstall</Pill>
  ) : /^SupportTicket/.test(type || "") ? (
    <Pill tone="brand" icon={LifeBuoy}>{type.replace(/^SupportTicket/, "Ticket ").toLowerCase()}</Pill>
  ) : (
    <Pill tone="ink">{type}</Pill>
  );

const LevelPill = ({ installType }) =>
  !installType ? null : installType === "Company" ? (
    <Pill tone="violet" icon={Building2}>Agency</Pill>
  ) : (
    <Pill tone="ink" icon={MapPin}>{installType}</Pill>
  );

const EventDrawer = ({ id, onClose }) => {
  const { logs } = useAppServices();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setEvent(null);
    setError("");
    logs.EventGet(id).then(({ response, error: err }) => {
      if (!alive) return;
      if (err || !response) setError(err?.message || "Could not load this event.");
      else setEvent(response);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const e = event;
  const installEvent = e && (e.type === "INSTALL" || e.type === "UNINSTALL");

  return (
    <Drawer
      onClose={onClose}
      label="App event"
      header={
        e ? (
          <div className="flex items-start gap-3">
            <AppIcon app={{ appId: e.appId, appName: e.appName, appImage: e.appImage }} size="md" />
            <div className="min-w-0">
              <h2 className="truncate text-[16px] font-semibold tracking-tight text-ink-900">{e.locationName || e.companyName || e.locationId || e.companyId || e.type}</h2>
              <p className="truncate text-[12.5px] text-ink-500">{e.appName || e.appId}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <TypePill type={e.type} />
                <LevelPill installType={e.installType} />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2 pt-1">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-24" />
          </div>
        )
      }
    >
      {error ? (
        <p className="m-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">{error}</p>
      ) : !e ? (
        <DrawerLoading />
      ) : (
        <>
          <InfoGrid
            rows={[
              ["Received", stamp(e.receivedAt)],
              ["Company", e.companyName],
              ["Location ID", e.locationId && <CopyId value={e.locationId} />],
              ["Company ID", e.companyId && <CopyId value={e.companyId} />],
              ["User ID", e.body?.userId && <CopyId value={e.body.userId} />],
              ["Webhook ID", e.webhookId && <CopyId value={e.webhookId} />],
            ]}
          />
          {installEvent && (
            <Section title="Install record" icon={Webhook}>
              <div className="flex items-start gap-2.5 rounded-xl border border-ink-200/70 bg-white px-3.5 py-2.5">
                {e.install ? <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-500" /> : <XCircle size={15} className="mt-0.5 shrink-0 text-rose-400" />}
                <p className="text-[12.5px] leading-relaxed text-ink-600">
                  {e.install
                    ? `${e.install.userType === "Company" ? "Agency" : "Location"} install row exists, created ${stamp(e.install.createdAt)} — status ${e.install.installStatus}.`
                    : "No install row matches this event. For an install, the connection never completed on our side."}
                </p>
              </div>
            </Section>
          )}
          <Section title="Body" icon={ScrollText}>
            <JsonBlock title="As received" value={e.body} />
          </Section>
        </>
      )}
    </Drawer>
  );
};

/* -------------------------------------------------------------------- page */

const AppEvents = () => {
  const { logs } = useAppServices();
  const toast = useToast();

  const [query, setQuery] = useListQuery(DEFAULTS);
  const [search, setSearch] = useSearchInput(query.q, (q) => setQuery({ q }));
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, limit: DEFAULTS.limit });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useOpenParam();

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: query.page, limit: query.limit });
    for (const key of ["appId", "type", "installType", "since", "q"]) if (query[key]) params.set(key, query[key]);
    const { response, error } = await logs.Events(params.toString());
    if (error) toast(error?.message || error?.error || "Could not load app events", "error");
    const next = response && Array.isArray(response.items) ? response : { items: [], total: 0, page: 1, pages: 1, limit: query.limit };
    if (next.items.length === 0 && next.total > 0 && query.page > next.pages) setQuery({ page: next.pages });
    setData(next);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.appId, query.type, query.installType, query.since, query.q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    logs.EventsStats().then(({ response }) => setStats(response || null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = Boolean(query.q || query.appId || query.type || query.installType || query.since);
  const clearFilters = () => setQuery({ q: "", appId: "", type: "", installType: "", since: "" });
  const closeDrawer = useCallback(() => setOpenId(null), [setOpenId]);
  const tickets = (stats?.types || []).filter((t) => /^SupportTicket/.test(t.type)).reduce((n, t) => n + t.count, 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats ? (
          <>
            <Stat label="Events kept" value={stats.total.toLocaleString()} hint="Last 90 days" icon={<Webhook size={16} />} tone="brand" />
            <Stat label="Installs this week" value={stats.installsWeek.toLocaleString()} hint="INSTALL events, last 7 days" icon={<Download size={16} />} tone="emerald" />
            <Stat label="Uninstalls this week" value={stats.uninstallsWeek.toLocaleString()} hint="UNINSTALL events, last 7 days" icon={<Unplug size={16} />} tone="amber" />
            <Stat label="Ticket events" value={tickets.toLocaleString()} hint="Support ticket created or updated" icon={<LifeBuoy size={16} />} tone="violet" />
          </>
        ) : (
          [0, 1, 2, 3].map((k) => <Skeleton key={k} className="h-[7.5rem] w-full" rounded="rounded-2xl" />)
        )}
      </div>

      <Card padded={false}>
        <ListFilters
          search={search}
          onSearch={setSearch}
          placeholder="Search company ID, location ID, company name or webhook ID"
          filters={[
            { label: "App", value: query.appId, onChange: (appId) => setQuery({ appId }), options: [["", "All apps"], ...(stats?.apps || []).map((a) => [a.appId, a.appName])], className: "w-48" },
            { label: "Event", value: query.type, onChange: (type) => setQuery({ type }), options: [["", "Any event"], ...(stats?.types || []).map((t) => [t.type, `${t.type} (${t.count})`])], className: "w-48" },
            { label: "Level", value: query.installType, onChange: (installType) => setQuery({ installType }), options: [["", "Any level"], ["Location", "Location"], ["Company", "Agency"]], className: "w-32" },
            { label: "Time", value: query.since, onChange: (since) => setQuery({ since }), options: [["", "Any time"], ["24h", "Last 24 hours"], ["7d", "Last 7 days"], ["30d", "Last 30 days"]], className: "w-36" },
          ]}
          onClear={clearFilters}
        />

        {loading && data.items.length === 0 ? (
          <div className="divide-y divide-ink-150">
            {[0, 1, 2, 3, 4].map((k) => (
              <div key={k} className="flex items-center gap-3 px-5 py-4">
                <Skeleton className="h-7 w-7" rounded="rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-1/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={<Webhook size={24} />}
            title={filtered ? "No events match" : "No events yet"}
            description={filtered ? "Try another app, filter or search term." : "Install, uninstall and ticket events from the marketplace appear here."}
          />
        ) : (
          <div className={cx("overflow-x-auto transition-opacity", loading && "opacity-60")}>
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-ink-150 text-left">
                  {["Received", "Event", "App", "Company", "Location"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-150">
                {data.items.map((e) => (
                  <tr key={e._id} onClick={() => setOpenId(e._id)} className={cx("cursor-pointer align-top transition-colors hover:bg-ink-50", openId === e._id && "bg-brand-50/50")}>
                    <td className="whitespace-nowrap px-4 py-3 text-[12px] text-ink-500" title={stamp(e.receivedAt)}>
                      {ago(e.receivedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <TypePill type={e.type} />
                        <LevelPill installType={e.installType} />
                      </div>
                    </td>
                    <td className="max-w-[13rem] px-4 py-3">
                      <div className="flex items-center gap-2">
                        <AppIcon app={{ appId: e.appId, appName: e.appName, appImage: e.appImage }} size="sm" className="!h-6 !w-6 !rounded-md !text-[9px]" />
                        <span className="truncate text-[12.5px] text-ink-700">{e.appName || e.appId}</span>
                      </div>
                    </td>
                    <td className="max-w-[14rem] px-4 py-3">
                      {e.companyName && <span className="block truncate text-[12.5px] text-ink-700">{e.companyName}</span>}
                      <span className="block truncate font-mono text-[11px] text-ink-400">{e.companyId || "—"}</span>
                    </td>
                    <td className="max-w-[14rem] px-4 py-3">
                      {e.locationName && <span className="block truncate text-[12.5px] text-ink-700">{e.locationName}</span>}
                      <span className="block truncate font-mono text-[11px] text-ink-400">{e.locationId || "—"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!(loading && data.items.length === 0) && (
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            limit={data.limit}
            noun={data.total === 1 ? "event" : "events"}
            onPage={(page) => setQuery({ page })}
            onLimit={(limit) => setQuery({ limit })}
          />
        )}
      </Card>

      {openId && <EventDrawer id={openId} onClose={closeDrawer} />}
    </div>
  );
};

export default AppEvents;
